import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactApexChart from 'react-apexcharts'
import ChartInteractions from './ChartInteractions'
import ChartLegend from './ChartLegend'
import useContainerWidth from '../../hooks/useContainerWidth'
import { useChartZoomSampling } from './hooks'
import {
  AXIS_LABEL_STYLE, AXIS_TITLE_STYLE, DS_COLORS, SINGLE_POINT_TOOLTIP_CLASSES, bucketShape,
  buildChartBaseOptions, singlePointOptions, storeGridCenter, withCategories
} from '../charts/config'

const CHART_ID = 'progression-controls-percent'

const CHART_CLASSES = 'tw:[&_.apexcharts-toolbar]:hidden!'

const LEGEND_ITEMS = [
  { label: 'Taux de satisfaction', lineClassName: 'tw:border-ds-info tw:border-solid' },
  { label: 'Taux d\'alerte', lineClassName: 'tw:border-ds-danger tw:border-dashed' }
]

const BASE_OPTIONS = {
  ...buildChartBaseOptions('area', CHART_ID),
  colors: [DS_COLORS.info, DS_COLORS.danger],
  stroke: { curve: 'smooth', width: 2, dashArray: [0, 5] },
  fill: {
    type: ['gradient', 'solid'],
    gradient: { opacityFrom: 0.45, opacityTo: 0.1 },
    opacity: [1, 0]
  },
  dataLabels: { enabled: false },
  yaxis: {
    max: 100,
    min: 0,
    tickAmount: 4,
    title: { text: 'Taux de satisfaction (%)', style: AXIS_TITLE_STYLE, offsetX: -6 },
    labels: { formatter: value => `${Math.round(value)} %`, style: AXIS_LABEL_STYLE }
  }
}

const satisfactionValue = satBucket => {
  if (!satBucket || satBucket.value === null || typeof satBucket.value !== 'number')
    return null
  return Math.round(satBucket.value)
}

const alertRate = (controlBucket, alertBucket) => {
  const total = controlBucket.count || 0
  const alertCount = alertBucket?.count || 0
  return total > 0 ? Math.round(alertCount / total * 100) : 0
}

const PercentChart = ({ alerts, controls, hasAlertsConfigured, satisfaction }) => {
  const containerRef = useRef(null)
  const containerWidth = useContainerWidth(containerRef)
  const handleZoomed = useChartZoomSampling(controls, containerWidth)

  const options = useMemo(() => {
    const opts = withCategories(BASE_OPTIONS, controls, { containerWidth, valueSuffix: ' %' })
    return {
      ...opts,
      ...singlePointOptions(controls),
      chart: {
        ...opts.chart,
        events: {
          ...opts.chart.events,
          mounted: storeGridCenter,
          updated: storeGridCenter,
          zoomed: handleZoomed
        }
      }
    }
  }, [controls, containerWidth, handleZoomed])

  const series = useMemo(() => {
    const satisfactionByPeriod = new Map(satisfaction.map(bucket => [bucket.period_start, bucket]))
    const alertsByPeriod = new Map(alerts.map(bucket => [bucket.period_start, bucket]))
    const satisfactionSerie = {
      name: 'Taux de satisfaction',
      data: controls.map(bucket => satisfactionValue(satisfactionByPeriod.get(bucket.period_start)))
    }
    if (!hasAlertsConfigured)
      return [satisfactionSerie]
    const alertSerie = {
      name: 'Taux d\'alerte',
      data: controls.map(bucket => alertRate(bucket, alertsByPeriod.get(bucket.period_start)))
    }
    return [satisfactionSerie, alertSerie]
  }, [alerts, controls, hasAlertsConfigured, satisfaction])

  return (
    <div
      ref={containerRef}
      className={`${CHART_CLASSES} ${controls.length === 1 ? SINGLE_POINT_TOOLTIP_CLASSES : ''}`.trim()}
    >
      <ReactApexChart options={options} series={series} type="area" height={320} />
      <ChartLegend
        actions={<ChartInteractions chartId={CHART_ID} />}
        items={hasAlertsConfigured ? LEGEND_ITEMS : []}
      />
    </div>
  )
}

PercentChart.propTypes = {
  alerts: PropTypes.arrayOf(bucketShape).isRequired,
  controls: PropTypes.arrayOf(bucketShape).isRequired,
  hasAlertsConfigured: PropTypes.bool.isRequired,
  satisfaction: PropTypes.arrayOf(bucketShape).isRequired
}

export default PercentChart
